import { isImageFile } from "@/components/ImageLightboxModal";
import { isIOS, isIPad } from "@/utils/Device";

export type FileViewerMode = "pdf" | "text" | "html" | "image" | "unsupported";

const TEXT_EXTENSIONS = new Set([
    "txt",
    "md",
    "markdown",
    "log",
    "csv",
    "tsv",
    "json",
    "xml",
    "yaml",
    "yml",
    "sql",
    "js",
    "ts",
    "tsx",
    "jsx",
    "php",
    "py",
    "rb",
    "sh",
    "bat",
    "ini",
    "cfg",
    "conf",
    "rtf",
    "properties",
]);

export const getFileViewerMode = (
    filename: string,
    mimeType = ""
): FileViewerMode => {
    const extension = filename.split(".").pop()?.toLowerCase() ?? "";
    const normalizedMime = mimeType.toLowerCase();

    if (isImageFile(filename, mimeType)) {
        return "image";
    }

    if (normalizedMime === "application/pdf" || extension === "pdf") {
        return "pdf";
    }

    if (["html", "htm"].includes(extension) || normalizedMime === "text/html") {
        return "html";
    }

    if (
        TEXT_EXTENSIONS.has(extension) ||
        normalizedMime.startsWith("text/") ||
        normalizedMime === "application/json" ||
        normalizedMime === "application/xml" ||
        normalizedMime === "application/javascript"
    ) {
        return "text";
    }

    return "unsupported";
};

export const canPreviewInApp = (filename: string, mimeType = ""): boolean => {
    return getFileViewerMode(filename, mimeType) !== "unsupported";
};

/** Browser-native PDF toolbars cannot hide download/save reliably (Chrome, Firefox, Safari). */
export const canHideNativePdfDownload = (): boolean => false;

/**
 * Use the browser PDF viewer when supported natively by the browser/platform.
 * On iOS/iPad or when browser PDF viewer is disabled, falls back to the react-pdf library.
 */
export const shouldUseNativePdfViewer = (_allowDownload = true): boolean => {
    if (typeof window === "undefined" || typeof navigator === "undefined") {
        return false;
    }

    if (isIOS() || isIPad()) {
        return false;
    }

    if ("pdfViewerEnabled" in navigator && !navigator.pdfViewerEnabled) {
        return false;
    }

    return true;
};
